import { HTMLAttributes } from 'react'
import Link from 'next/link'

import Button from '@/components/base/button'
import Container from '@/components/base/container'
import Form from '@/components/base/form'
import Grid from '@/components/base/grid'
import Motion from '@/components/base/motion'
import Section from '@/components/base/section'
import TextEffect from '@/components/base/text-effect'
import Typography from '@/components/base/typography'

import { contactRevealAnimation } from './animations/contact'
import { WPGravityForm } from '@/types'

export type ContactProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    title: string
    sections: any[]
    form_title: string
    form_paragraph: boolean
    form: WPGravityForm
  }>

const Contact = ({
  title,
  sections,
  form_title,
  form_paragraph,
  form,
  ...props
}: ContactProps) => {
  return (
    <Motion animation={contactRevealAnimation}>
      {(motionRef) => (
        <Section ref={motionRef} {...props}>
          <Container>
            <Grid className="gap-y-spacing-lg">
              <div className="col-span-2 md:col-span-3 lg:col-span-6">
                <div
                  className="flex flex-col gap-y-spacing-sm"
                  data-motion-column
                >
                  {title ? (
                    <Typography
                      variant="h2"
                      component="h1"
                      className="font-medium text-text-accent-light dark:text-text-accent-dark"
                    >
                      {title}
                    </Typography>
                  ) : null}
                  {sections?.map((section, index) => {
                    if (section.acf_fc_layout === 'subtitle') {
                      return (
                        <Typography key={index} variant="large" paragraph>
                          {section.content}
                        </Typography>
                      )
                    }
                    if (section.acf_fc_layout === 'paragraph') {
                      return (
                        <Typography key={index} variant="regular" paragraph>
                          {section.content}
                        </Typography>
                      )
                    }
                    if (section.acf_fc_layout === 'buttons') {
                      return (
                        <div
                          key={index}
                          className="flex flex-wrap gap-spacing-xs"
                        >
                          {section.items.map((item: any, index: number) => {
                            return item.link.url ? (
                              <Button
                                key={index}
                                variant={item.variant}
                                asChild
                                isLiquid
                              >
                                <Link
                                  href={item.link.url}
                                  scroll={false}
                                  target={item.link.target || '_self'}
                                >
                                  <TextEffect>
                                    <Typography variant="regular">
                                      {item.link.title}
                                    </Typography>
                                  </TextEffect>
                                </Link>
                              </Button>
                            ) : null
                          })}
                        </div>
                      )
                    }
                    return null
                  })}
                </div>
              </div>
              <div className="col-span-2 md:col-span-3 lg:col-span-6">
                <div
                  className="flex flex-col gap-y-spacing-sm"
                  data-motion-column
                >
                  {form_title ? (
                    <Typography variant="h4" component="h2">
                      {form_title}
                    </Typography>
                  ) : null}
                  {form_paragraph ? (
                    <Typography variant="regular" paragraph>
                      {form_paragraph}
                    </Typography>
                  ) : null}
                  {form?.id ? (
                    <Form
                      form_id={form.id}
                      fields={form?.fields}
                      submit={form?.button?.text}
                      theme="light"
                    />
                  ) : null}
                </div>
              </div>
            </Grid>
          </Container>
        </Section>
      )}
    </Motion>
  )
}

export default Contact
